<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { useEventListener } from '@vueuse/core';

import { useBrowserStore } from '@apps/main/new-desktop/core/stores';

import { AppMarkdownWrapper, AppSvgIcon, AppTooltip } from '@libs/ui';

import type { HotelRoom } from '../../../models';

const browserStore = useBrowserStore();

interface HotelStayCardRoomProps {
  room: HotelRoom;
}

const props = defineProps<HotelStayCardRoomProps>();

const roomNameRef = ref<HTMLElement | null>(null);
const isRoomNameClamped = ref(false);

function styleHeading(): void {
  if (roomNameRef.value) {
    isRoomNameClamped.value =
      roomNameRef.value.scrollHeight > roomNameRef.value.clientHeight;
  }
}

useEventListener(window, 'resize', styleHeading);

onMounted(() => {
  styleHeading();
});
</script>

<template>
  <div class="hotel-stay-card-room">
    <div class="hotel-stay-header">
      <div class="hotel-stay-heading">
        <AppTooltip
          v-if="isRoomNameClamped"
          position="top-right"
          :ariaLabel="props.room.name"
        >
          <template #trigger>
            <div class="room-name-wrapper">
              <span ref="roomNameRef" class="room-name">
                {{ props.room.name }}
              </span>
              <AppSvgIcon
                v-if="browserStore.isSmallerThan1200px"
                name="infoCircle"
                size="18"
              />
            </div>
          </template>
          <template #default>
            {{ props.room.name }}
          </template>
        </AppTooltip>
        <span v-else ref="roomNameRef" class="room-name">
          {{ props.room.name }}
        </span>
      </div>
      <div class="hotel-stay-paxes">
        {{ props.room.paxString }}
      </div>
      <AppMarkdownWrapper :source="props.room.description" />
    </div>
  </div>
</template>

<style scoped lang="scss">
.hotel-stay-card-room {
  @include font-body-m-light;
  color: var(--color-neutral-70);

  .hotel-stay-header {
    display: flex;
    flex-direction: column;
    gap: var(--spacing-4);

    .hotel-stay-heading {
      color: var(--color-neutral-70);
      @include font-body-m-bold;

      .room-name-wrapper {
        display: flex;
        flex-direction: row;
        flex-wrap: nowrap;
        gap: var(--spacing-12);
      }
      .room-name {
        display: -webkit-box;
        line-clamp: 2;
        -webkit-line-clamp: 2;
        -webkit-box-orient: vertical;
        overflow: hidden;
        text-overflow: ellipsis;
      }
    }

    .hotel-stay-paxes {
      color: var(--color-neutral-70);
      @include font-label-s-light;
    }
  }
}
</style>
